import { ApiResult } from 'src/Libs/api'
import { AuthFetchWithResponse } from 'src/Types/api'
import { FetchProps } from '@e3dc-react/shell/Hooks/AuthFetch/AuthFetch'
import { StatusCodes } from '@e3dc-react/shell/Libs/http-status-codes'
import { AuthFetchError } from '@e3dc-react/shell/Types'
import { handleFetchError } from 'src/Libs/api/utils'
import { UserInvitationDto } from '../dto'
import { userInvitationsToModelTransformer } from '../transformer'
import { UserInvitationModel } from '../models'

/**
 * Removes an existing invitation.
 *
 * @param authFetch
 * @param token
 */
const removeInvitation = async (authFetch: AuthFetchWithResponse, token: string): Promise<ApiResult<null>> => {
  const fetchProps = {
    path: `installations/invitations/${token}`,
    endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    method: 'delete',
  } as FetchProps

  const response = await authFetch<void>(fetchProps)

  return new ApiResult(null, response.status === StatusCodes.CREATED)
}

/**
 * Request to resend a notification (email, ...) as a reminder to an existing invitation.
 *
 * @param authFetch
 * @param token
 */
const resendInvitation = async (authFetch: AuthFetchWithResponse, token: string, installationId: string): Promise<ApiResult<null>> => {
  const fetchProps = {
    path: `installations/${installationId}/invitations/${token}/resend`,
    endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    method: 'post',
  } as FetchProps

  const response = await authFetch<void>(fetchProps)

  return new ApiResult(null, response.status === StatusCodes.CREATED)
}
/**
 * Accepts an existing invitation.
 *
 * @param authFetch
 * @param token
 */
const acceptInvitationToken = async (authFetch: AuthFetchWithResponse, token: string): Promise<ApiResult<null>> => {
  try {
    const response = await authFetch({
      path: `installations/invitations/${token}/accept`,
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      method: 'POST',
    })

    if (response.status !== StatusCodes.OK && response.status !== StatusCodes.CREATED && response.status !== StatusCodes.NO_CONTENT) {
      return new ApiResult<null, Error | AuthFetchError>(null, true)
    }

    return new ApiResult<null>(null)
  } catch (error) {
    return handleFetchError<null>(error)
  }
}

/**
 * Fetches all open invitations for a given user uid.
 * Used to display the invitation pop-up after login
 *
 * @param uid - the user's unique identifier (uid)
 * @param authFetch
 */
export const fetchUserInvitations = async (uid: number, authFetch: AuthFetchWithResponse): Promise<ApiResult<UserInvitationModel[], Error>> => {
  try {
    const response = await authFetch<UserInvitationDto[]>({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/invitations/by-user/${uid}`,
      method: 'GET',
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult(userInvitationsToModelTransformer(response.data))
    }

    return new ApiResult<UserInvitationModel[], Error>(null, true)
  } catch (error) {
    return handleFetchError<UserInvitationModel[]>(error)
  }
}

const allApis = { acceptInvitationToken, removeInvitation, resendInvitation, fetchUserInvitations }

export default allApis
